import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MEDIA } from "@/lib/collection";
import { MaskLine } from "@/components/site/Reveal";
import { scrollToId } from "@/components/site/SmoothScroll";

const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.1]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] w-full overflow-hidden bg-[#faf8f5]"
    >
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0"
      >
        <img
          src={MEDIA.heroStaircase}
          alt="A bold Murakami-inspired living room with a hand-painted blue X on the wall, captioned « The Murakami goes here »"
          className="h-full w-full object-cover"
          style={{ objectPosition: "50% 38%" }}
          data-testid="hero-image"
        />
      </motion.div>

      {/* readability gradients */}
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-gradient-to-t from-[#111111]/70 via-[#111111]/10 to-transparent"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#111111]/25 to-transparent" />

      <div className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-end px-6 md:px-12 pb-16 md:pb-20">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.15, ease: EASE }}
          data-testid="hero-eyebrow"
          className="mb-5 text-xs md:text-sm uppercase tracking-[0.35em] text-[#faf8f5]/90"
        >
          Imaginary Collection
        </motion.p>

        <h1
          data-testid="hero-title"
          className="font-display text-[#faf8f5] leading-[0.92] tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-8xl"
        >
          <MaskLine delay={0.35}>
            The <span className="italic">X</span> Goes
          </MaskLine>
          <MaskLine delay={0.5}>Here.</MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.9, ease: EASE }}
          data-testid="hero-subtitle"
          className="mt-7 max-w-xl text-base md:text-lg font-light text-[#faf8f5]/85"
        >
          The art you dream of owning — hand-painted, for real, on your own
          walls.
        </motion.p>

        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          onClick={() => scrollToId("concept")}
          data-testid="hero-scroll-cue"
          className="mt-10 flex items-center gap-3 text-sm font-light tracking-wide text-[#faf8f5]/90"
        >
          <span className="grid h-10 w-10 place-items-center rounded-full border border-[#faf8f5]/40">
            <ArrowDown className="h-4 w-4 animate-bounce" strokeWidth={1.5} />
          </span>
          Discover the concept
        </motion.button>
      </div>
    </section>
  );
}
